import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Printer, FileText, Loader2, Building2, Send, Link2, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/utils/constants";

const fmt = (v, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(v || 0);
const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
  sent: { label: "Sent", cls: "bg-sky-500/15 text-sky-600" },
  paid: { label: "Paid", cls: "bg-emerald-500/15 text-emerald-600" },
  overdue: { label: "Overdue", cls: "bg-red-500/15 text-red-600" },
};
const EMPTY_ITEM = { description: "", qty: 1, unit_price: 0 };

const printInvoice = (inv, biz = {}) => {
  const cur = inv.currency || "USD";
  const F = (v) => fmt(v, cur);
  const hasBiz = !!(biz.business_name || "").trim();
  const logoImg = biz.logo ? `<img src="${biz.logo}" alt="logo" style="max-height:56px;max-width:200px;margin-bottom:8px;display:block;" />` : "";
  const brandBlock = hasBiz
    ? `<div>${logoImg}<div style="font-size:24px;font-weight:800;color:#0284C7;">${biz.business_name}</div>
       ${biz.business_address ? `<div style="font-size:12px;color:#6B7280;white-space:pre-line;">${biz.business_address}</div>` : ""}
       <div style="font-size:12px;color:#6B7280;">${[biz.business_email, biz.business_phone].filter(Boolean).join(" · ")}</div>
       ${biz.business_website ? `<div style="font-size:12px;color:#6B7280;">${biz.business_website}</div>` : ""}
       ${biz.tax_id ? `<div style="font-size:11px;color:#9CA3AF;margin-top:2px;">Tax ID: ${biz.tax_id}</div>` : ""}</div>`
    : `<div>${logoImg}<div style="font-size:24px;font-weight:800;color:#0284C7;">Invoice</div><div style="font-size:11px;color:#9CA3AF;">Set your business details in the Invoices tab to brand this header</div></div>`;
  const rows = (inv.items || []).map((i) => `<tr><td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;">${i.description}</td><td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;text-align:center;">${i.qty}</td><td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;text-align:right;">${F(i.unit_price)}</td><td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;text-align:right;">${F((i.qty || 0) * (i.unit_price || 0))}</td></tr>`).join("");
  const html = `<!doctype html><html><head><title>${inv.number} — ${hasBiz ? biz.business_name : "Invoice"}</title></head>
<body style="font-family:Arial,Helvetica,sans-serif;color:#111827;margin:0;padding:40px;max-width:760px;margin:0 auto;">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;">
    ${brandBlock}
    <div style="text-align:right;"><div style="font-size:28px;font-weight:800;">INVOICE</div><div style="font-size:14px;color:#6B7280;">${inv.number}</div>
    <div style="margin-top:6px;display:inline-block;padding:3px 12px;border-radius:99px;font-size:11px;font-weight:700;background:${inv.status === "paid" ? "#D1FAE5;color:#065F46" : inv.status === "overdue" ? "#FEE2E2;color:#991B1B" : "#E0F2FE;color:#075985"};">${(STATUS_META[inv.status] || STATUS_META.draft).label.toUpperCase()}</div></div>
  </div>
  <div style="display:flex;justify-content:space-between;margin-bottom:28px;">
    <div><div style="font-size:11px;color:#6B7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Billed to</div>
      <div style="font-weight:700;">${inv.client_name || ""}</div>
      ${inv.client_company ? `<div style="font-size:13px;color:#374151;">${inv.client_company}</div>` : ""}
      ${inv.client_email ? `<div style="font-size:13px;color:#6B7280;">${inv.client_email}</div>` : ""}</div>
    <div style="text-align:right;font-size:13px;color:#374151;">
      <div><span style="color:#6B7280;">Issue date:</span> ${inv.issue_date || "—"}</div>
      <div><span style="color:#6B7280;">Due date:</span> ${inv.due_date || "—"}</div></div>
  </div>
  <table style="width:100%;border-collapse:collapse;font-size:13px;">
    <thead><tr style="background:#F9FAFB;"><th style="padding:10px 12px;text-align:left;border-bottom:2px solid #E5E7EB;">Description</th><th style="padding:10px 12px;text-align:center;border-bottom:2px solid #E5E7EB;">Qty</th><th style="padding:10px 12px;text-align:right;border-bottom:2px solid #E5E7EB;">Unit price</th><th style="padding:10px 12px;text-align:right;border-bottom:2px solid #E5E7EB;">Amount</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-top:16px;">
    <table style="font-size:13px;min-width:240px;">
      <tr><td style="padding:4px 12px;color:#6B7280;">Subtotal</td><td style="padding:4px 0;text-align:right;">${F(inv.subtotal)}</td></tr>
      <tr><td style="padding:4px 12px;color:#6B7280;">Tax (${inv.tax_rate || 0}%)</td><td style="padding:4px 0;text-align:right;">${F(inv.tax)}</td></tr>
      <tr><td style="padding:8px 12px;font-weight:800;font-size:16px;border-top:2px solid #111827;">Total</td><td style="padding:8px 0;text-align:right;font-weight:800;font-size:16px;border-top:2px solid #111827;">${F(inv.total)}</td></tr>
    </table>
  </div>
  ${inv.notes ? `<div style="margin-top:28px;padding:14px 16px;background:#F9FAFB;border-radius:10px;font-size:12px;color:#374151;"><strong>Notes:</strong> ${inv.notes}</div>` : ""}
  <script>window.onload = () => setTimeout(() => window.print(), 200);</script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
  w.document.write(html);
  w.document.close();
};

export const CRMInvoices = ({ refreshKey }) => {
  const [invoices, setInvoices] = useState(null);
  const [editor, setEditor] = useState(null);
  const [biz, setBiz] = useState({});
  const [bizOpen, setBizOpen] = useState(false);
  const [bizForm, setBizForm] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/crm/invoices`); setInvoices(r.data); }
    catch { setInvoices([]); toast.error("Failed to load invoices"); }
    try { const s = await axios.get(`${API}/crm/settings`); setBiz(s.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const saveBiz = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.put(`${API}/crm/settings`, bizForm);
      setBiz(r.data); setBizOpen(false);
      toast.success("Business details saved — they'll appear on all your invoices");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save business details"); }
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Image too large — max 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 400 / img.width, 160 / img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setBizForm((f) => ({ ...f, logo: canvas.toDataURL("image/png") }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      client_name: editor.client_name, client_email: editor.client_email || "", client_company: editor.client_company || "",
      items: editor.items.filter((i) => i.description.trim()).map((i) => ({ description: i.description, qty: parseFloat(i.qty) || 0, unit_price: parseFloat(i.unit_price) || 0 })),
      tax_rate: parseFloat(editor.tax_rate) || 0, notes: editor.notes || "", status: editor.status || "draft",
      issue_date: editor.issue_date || null, due_date: editor.due_date || null, recurring: editor.recurring || "none", currency: editor.currency || "USD",
    };
    try {
      if (editor.id) await axios.put(`${API}/crm/invoices/${editor.id}`, payload);
      else await axios.post(`${API}/crm/invoices`, payload);
      setEditor(null); load(); toast.success("Invoice saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save invoice"); }
  };

  const setStatus = async (id, status) => {
    try { await axios.put(`${API}/crm/invoices/${id}/status`, { status }); load(); }
    catch { toast.error("Failed to update status"); }
  };

  const [sendingId, setSendingId] = useState(null);
  const sendInvoice = async (inv) => {
    if (!inv.client_email) { toast.error("Add a client email to this invoice first — edit it and fill in the email"); return; }
    if (!window.confirm(`Email invoice ${inv.number} (${fmt(inv.total)}) to ${inv.client_email}?`)) return;
    setSendingId(inv.id);
    try {
      const r = await axios.post(`${API}/crm/invoices/${inv.id}/send`);
      toast.success(`Invoice sent to ${r.data.to}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send invoice"); }
    setSendingId(null);
  };

  const copyLinks = async (inv) => {
    try {
      const r = await axios.post(`${API}/crm/invoices/${inv.id}/share-links`);
      const url = `${window.location.origin}${r.data.pay_path}`;
      await navigator.clipboard.writeText(url);
      toast.success(r.data.portal_path
        ? `Payment link copied! Client portal: ${window.location.origin}${r.data.portal_path}`
        : "Payment link copied to clipboard");
    } catch { toast.error("Failed to create link"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await axios.delete(`${API}/crm/invoices/${id}`); load(); toast.success("Invoice deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const setItem = (idx, field, val) => setEditor({ ...editor, items: editor.items.map((it, i) => (i === idx ? { ...it, [field]: val } : it)) });
  const subtotal = editor ? editor.items.reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0) : 0;
  const tax = subtotal * ((editor && parseFloat(editor.tax_rate)) || 0) / 100;

  if (invoices === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="crm-invoices">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">{invoices.length} invoice{invoices.length === 1 ? "" : "s"}</p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { setBizForm({ business_name: biz.business_name || "", business_email: biz.business_email || "", business_phone: biz.business_phone || "", business_address: biz.business_address || "", business_website: biz.business_website || "", tax_id: biz.tax_id || "", logo: biz.logo || "", payment_link: biz.payment_link || "", payment_instructions: biz.payment_instructions || "" }); setBizOpen(true); }} data-testid="business-details-btn">
            <Building2 className="h-4 w-4 mr-1.5" />{biz.business_name ? biz.business_name : "Business details"}
          </Button>
          <Button size="sm" onClick={() => setEditor({ client_name: "", client_email: "", client_company: "", items: [{ ...EMPTY_ITEM }], tax_rate: 0, notes: "", status: "draft", issue_date: new Date().toISOString().slice(0, 10), due_date: "", recurring: "none", currency: "USD" })} data-testid="add-invoice-btn"><Plus className="h-4 w-4 mr-1" />Invoice</Button>
        </div>
      </div>
      {!biz.business_name && invoices.length > 0 && (
        <p className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2 mb-4" data-testid="business-details-hint">
          Tip: set your <b>Business details</b> so your company name and info appear on printed invoices.
        </p>
      )}

      {invoices.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No invoices yet — create one, or open a deal and click "Create invoice".</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Number</th><th className="text-left p-3 font-medium">Client</th><th className="text-right p-3 font-medium">Total</th><th className="text-left p-3 font-medium">Status</th><th className="text-left p-3 font-medium hidden md:table-cell">Due</th><th className="p-3 w-28"></th></tr></thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t hover:bg-muted/30" data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-3 font-medium">{inv.number}{inv.recurring === "monthly" && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-600" title="Repeats monthly" data-testid={`recurring-badge-${inv.id}`}><Repeat className="h-2.5 w-2.5" />M</span>}</td>
                  <td className="p-3"><div>{inv.client_name}</div>{inv.client_company && <div className="text-[11px] text-muted-foreground">{inv.client_company}</div>}</td>
                  <td className="p-3 text-right font-semibold">{fmt(inv.total, inv.currency)}</td>
                  <td className="p-3">
                    <Select value={inv.status} onValueChange={(v) => setStatus(inv.id, v)}>
                      <SelectTrigger className={`h-7 w-[110px] text-xs border-0 font-medium ${STATUS_META[inv.status]?.cls || ""}`} data-testid={`invoice-status-${inv.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-muted-foreground hidden md:table-cell">{inv.due_date || "—"}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => copyLinks(inv)} className="p-1.5 rounded hover:bg-violet-500/10 text-muted-foreground hover:text-violet-600" title="Copy payment link" data-testid={`copy-link-invoice-${inv.id}`}><Link2 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => sendInvoice(inv)} disabled={sendingId === inv.id} className="p-1.5 rounded hover:bg-sky-500/10 text-muted-foreground hover:text-sky-600 disabled:opacity-40" title={inv.client_email ? `Email to ${inv.client_email}` : "No client email set"} data-testid={`send-invoice-${inv.id}`}>
                        {sendingId === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => printInvoice(inv, biz)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Print / PDF" data-testid={`print-invoice-${inv.id}`}><Printer className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setEditor({ ...inv, items: inv.items?.length ? inv.items : [{ ...EMPTY_ITEM }] })} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`edit-invoice-${inv.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(inv.id)} className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="invoice-drawer">
          <SheetHeader><SheetTitle>{editor?.id ? `Edit ${editor.number}` : "New Invoice"}</SheetTitle></SheetHeader>
          {editor && (
            <form onSubmit={save} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Client name</label>
                <Input required placeholder="Client name" value={editor.client_name} onChange={(e) => setEditor({ ...editor, client_name: e.target.value })} data-testid="invoice-client-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Client email</label>
                  <Input type="email" placeholder="client@company.com" value={editor.client_email} onChange={(e) => setEditor({ ...editor, client_email: e.target.value })} data-testid="invoice-client-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Company</label>
                  <Input placeholder="Company" value={editor.client_company} onChange={(e) => setEditor({ ...editor, client_company: e.target.value })} data-testid="invoice-client-company" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Issue date</label>
                  <Input type="date" value={editor.issue_date || ""} onChange={(e) => setEditor({ ...editor, issue_date: e.target.value })} data-testid="invoice-issue-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Due date</label>
                  <Input type="date" value={editor.due_date || ""} onChange={(e) => setEditor({ ...editor, due_date: e.target.value })} data-testid="invoice-due-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Status</label>
                  <Select value={editor.status} onValueChange={(v) => setEditor({ ...editor, status: v })}>
                    <SelectTrigger data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Line items</label>
                <div className="space-y-2">
                  {editor.items.map((it, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className="flex-1" data-testid={`invoice-item-desc-${i}`} />
                      <Input type="number" min="0" step="0.5" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="w-16" data-testid={`invoice-item-qty-${i}`} />
                      <Input type="number" min="0" step="0.01" placeholder="Price" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="w-24" data-testid={`invoice-item-price-${i}`} />
                      <button type="button" onClick={() => setEditor({ ...editor, items: editor.items.filter((_, x) => x !== i) })} disabled={editor.items.length === 1} className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500 disabled:opacity-30 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => setEditor({ ...editor, items: [...editor.items, { ...EMPTY_ITEM }] })} data-testid="invoice-add-item-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add item</Button>
              </div>

              <div className="flex items-end justify-between gap-4">
                <div className="w-28">
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Tax %</label>
                  <Input type="number" min="0" step="0.1" value={editor.tax_rate} onChange={(e) => setEditor({ ...editor, tax_rate: e.target.value })} data-testid="invoice-tax-rate" />
                </div>
                <div className="w-36">
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Repeats</label>
                  <Select value={editor.recurring || "none"} onValueChange={(v) => setEditor({ ...editor, recurring: v })}>
                    <SelectTrigger data-testid="invoice-recurring-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">One-time</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Currency</label>
                  <Select value={editor.currency || "USD"} onValueChange={(v) => setEditor({ ...editor, currency: v })}>
                    <SelectTrigger data-testid="invoice-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-right text-sm space-y-0.5 flex-1" data-testid="invoice-totals">
                  <div className="text-xs text-muted-foreground">Subtotal {fmt(subtotal, editor.currency)} · Tax {fmt(tax, editor.currency)}</div>
                  <div className="font-bold text-lg">Total {fmt(subtotal + tax, editor.currency)}</div>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Notes</label>
                <Textarea rows={2} placeholder="Payment terms, bank details…" value={editor.notes || ""} onChange={(e) => setEditor({ ...editor, notes: e.target.value })} data-testid="invoice-notes" />
              </div>

              <div className="flex justify-between pt-1">
                {editor.id && <Button type="button" variant="outline" size="sm" onClick={() => printInvoice({ ...editor, subtotal, tax, total: subtotal + tax })} data-testid="invoice-print-btn"><Printer className="h-4 w-4 mr-1.5" />Print / PDF</Button>}
                <Button type="submit" className="ml-auto" data-testid="save-invoice-btn">Save Invoice</Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={bizOpen} onOpenChange={setBizOpen}>
        <DialogContent className="max-w-md" data-testid="business-details-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" />Your business details</DialogTitle></DialogHeader>
          {bizForm && (
            <form onSubmit={saveBiz} className="space-y-3">
              <p className="text-xs text-muted-foreground">Saved once, used as the sender header on every invoice you print.</p>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Company logo</label>
                <div className="flex items-center gap-3">
                  {bizForm.logo ? (
                    <div className="relative group">
                      <img src={bizForm.logo} alt="Company logo" className="h-14 max-w-[160px] object-contain rounded-lg border bg-white p-1" data-testid="biz-logo-preview" />
                      <button type="button" onClick={() => setBizForm({ ...bizForm, logo: "" })} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow hover:bg-red-600" data-testid="biz-logo-remove">×</button>
                    </div>
                  ) : (
                    <div className="h-14 w-24 rounded-lg border border-dashed flex items-center justify-center text-[10px] text-muted-foreground">No logo</div>
                  )}
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center h-8 px-3 rounded-md border text-xs font-medium hover:bg-muted transition-colors">{bizForm.logo ? "Replace logo" : "Upload logo"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { handleLogoFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="biz-logo-input" />
                  </label>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">PNG/JPG, auto-resized. Shown at the top of printed invoices.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Company / business name</label>
                <Input required placeholder="Acme GmbH" value={bizForm.business_name} onChange={(e) => setBizForm({ ...bizForm, business_name: e.target.value })} data-testid="biz-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email</label>
                  <Input type="email" placeholder="billing@acme.com" value={bizForm.business_email} onChange={(e) => setBizForm({ ...bizForm, business_email: e.target.value })} data-testid="biz-email-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Phone</label>
                  <Input placeholder="+1 555 000 0000" value={bizForm.business_phone} onChange={(e) => setBizForm({ ...bizForm, business_phone: e.target.value })} data-testid="biz-phone-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Address</label>
                <Textarea rows={2} placeholder="Street, City, ZIP, Country" value={bizForm.business_address} onChange={(e) => setBizForm({ ...bizForm, business_address: e.target.value })} data-testid="biz-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Website</label>
                  <Input placeholder="acme.com" value={bizForm.business_website} onChange={(e) => setBizForm({ ...bizForm, business_website: e.target.value })} data-testid="biz-website-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Tax / VAT ID</label>
                  <Input placeholder="DE123456789" value={bizForm.tax_id} onChange={(e) => setBizForm({ ...bizForm, tax_id: e.target.value })} data-testid="biz-taxid-input" />
                </div>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs font-semibold mb-2">How clients pay you</p>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Your payment link <span className="font-normal">(Stripe Payment Link, PayPal.me, etc.)</span></label>
                  <Input placeholder="https://buy.stripe.com/… or https://paypal.me/…" value={bizForm.payment_link} onChange={(e) => setBizForm({ ...bizForm, payment_link: e.target.value })} data-testid="biz-payment-link-input" />
                </div>
                <div className="mt-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Payment instructions <span className="font-normal">(bank transfer / IBAN, etc.)</span></label>
                  <Textarea rows={2} placeholder="Bank: … IBAN: … Reference: invoice number" value={bizForm.payment_instructions} onChange={(e) => setBizForm({ ...bizForm, payment_instructions: e.target.value })} data-testid="biz-payment-instructions-input" />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">Shown on your public invoice pages and emails. If a payment link is set, the "Pay" button sends clients there.</p>
              </div>
              <Button type="submit" className="w-full" data-testid="save-biz-btn">Save as default</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
